import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAppStore } from '@/store';
import LoadingScreen from '@/components/ui/loading-screen';
import AuthPage from '@/pages/Auth';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Index from '@/pages/Index';
import Chat from '@/pages/Chat';
import LobbyPage from '@/pages/Lobby';
import MCPServices from '@/pages/MCPServices';
import MCPGuide from '@/pages/MCPGuide'; // public MCP connection guide
import Users from '@/pages/Users';
import Settings from '@/pages/Settings';
import Profile from '@/pages/Profile';
import AgentMarketplace from '@/pages/agents/AgentMarketplace';
import Eula from '@/pages/Eula';

import About from '@/pages/About';
import News from '@/pages/News';
import NewsDetail from '@/pages/NewsDetail';
import Purchase from '@/pages/Purchase';
import Community from '@/pages/Community';

import NotFound from '@/pages/NotFound';

import ChangelogPage from '@/pages/Changelog';
import LandingPage from '@/pages/LandingPage';
import WelcomePage from '@/pages/WelcomePage';
import Research from '@/pages/Research';
import Reports from '@/pages/Reports';
import Partnership from '@/pages/Partnership';
import { initGA, trackPageView } from '@/lib/ga';

import { loadRewardfulScript } from '@/utils/rewardful';
import CompleteRegistrationPage from '@/pages/CompleteRegistration';
import Privacy from '@/pages/Privacy';
import MaplePT from '@/pages/MaplePT';
import MapleOS from '@/pages/MapleOS';
import MapleNode from '@/pages/MapleNode';
import AICredits from '@/pages/AICredits';
import Models from '@/pages/Models';
import AccessControl from '@/pages/AccessControl';
import FinetuneChat from '@/pages/FinetuneChat';
import SupportInbox from '@/pages/SupportInbox';
import SupportOpen from '@/pages/SupportOpen';
import SeoTopicPage from '@/pages/SeoTopicPage';
import Insights from '@/pages/Insights';
import InsightArticlePage from '@/pages/InsightArticlePage';

import MainLayout from '@/components/layout/MainLayout';
import IntelligenceXP from '@/pages/IntelligenceXP';
import DeviceLink from '@/pages/DeviceLink'; // CanXP Code device-login approval

const PUBLIC_ROUTE_PREFIXES = [
  '/auth',
  '/complete-registration',
  '/',
  '/data',
  '/data-residency',
  '/security',
  '/compliance',
  '/privacy',
  '/eula',
  '/about',
  '/news',
  '/purchase',
  '/community',
  '/welcome',
  '/research',
  '/reports',
  '/enterprise',
  '/partnership',
  '/models',
  '/maplept',
  '/mapleos',
  '/maplenode',
  '/changelog',
  '/sovereign-ai-canada',
  '/ai-model-training',
  '/small-language-model-training',
  '/private-ai',
  '/pipeda-compliant-ai',
  '/healthcare-ai-canada',
  '/legal-ai-canada',
  '/canadian-ai-infrastructure',
  '/rag-vs-fine-tuning',
  '/canadian-ai-platform',
  '/enterprise-ai',
  '/insights',
  '/support/open',
  '/link',
  '/mcp',
];

export default function AppInitializer() {
  const { loading: authLoading, user } = useAuth();
  const { profile } = useProfile();
  const { fetchChats, fetchModels } = useSupabaseStore();
  const { textSize, saveHistory, allowAnalytics } = useAppStore();
  const location = useLocation();

  useEffect(() => {
    if (allowAnalytics) {
      initGA();
      loadRewardfulScript('a6af9b');
    }
  }, [allowAnalytics]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const via = params.get("via");

    if (via) {
      localStorage.setItem("rewardful_referral", via);
    } else {
      const savedVia = localStorage.getItem("rewardful_referral");
      if (savedVia) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("via", savedVia);
        window.history.replaceState({}, "", newUrl.toString());
      }
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchChats(user.id);
      fetchModels();
    }
  }, [authLoading, user, fetchChats, fetchModels]);

  useEffect(() => {
    const isNewsArticle = /^\/news\/[^/]+/.test(location.pathname);
    if (!allowAnalytics || isNewsArticle) return;

    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        trackPageView(location.pathname + location.search, document.title);
      });
    });

    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [allowAnalytics, location.pathname, location.search]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (location.hash) {
        const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));

        if (target) {
          target.scrollIntoView({ block: 'start' });
          return;
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, location.hash]);

  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) =>
    prefix === '/'
      ? location.pathname === '/'
      : location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );

  const isLoading = !isPublicRoute && authLoading;

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/complete-registration" element={<CompleteRegistrationPage />} />

      <Route
        path="/"
        element={
          <MainLayout>
            <LandingPage />
          </MainLayout>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        }
      />

      <Route path="/chat/:chatId" element={
        <ProtectedRoute><Chat /></ProtectedRoute>
      } />
      <Route path="/lobby" element={
        <ProtectedRoute><LobbyPage /></ProtectedRoute>
      } />
      <Route path="/lobby/:channelSlug" element={
        <ProtectedRoute><LobbyPage /></ProtectedRoute>
      } />
      <Route path="/projects" element={<Navigate to="/" replace />} />
      <Route path="/projects/:projectId" element={<Navigate to="/" replace />} />
      <Route path="/blueprints" element={<Navigate to="/" replace />} />
      <Route path="/blueprints/:blueprintId" element={<Navigate to="/" replace />} />
      <Route path="/canvas" element={<Navigate to="/" replace />} />
      <Route path="/code" element={<Navigate to="/" replace />} />
      <Route path="/kb" element={<Navigate to="/" replace />} />
      {/* Public guide for connecting customer data over MCP. Deliberately NOT ProtectedRoute:
          this is the link handed to a prospect who has no account yet. The authenticated MCP
          Services registry moved to /mcp-services. */}
      <Route path="/mcp" element={<MCPGuide />} />
      <Route path="/mcp-services" element={
        <ProtectedRoute><MCPServices /></ProtectedRoute>
      } />
      <Route path="/intelligencexp" element={
        <ProtectedRoute><IntelligenceXP /></ProtectedRoute>
      } />
      <Route path="/system-graph" element={<Navigate to="/intelligencexp" replace />} />

      <Route path="/users" element={
        <ProtectedRoute><Users /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/support/open" element={<SupportOpen />} />
      <Route path="/support/inbox" element={
        <ProtectedRoute><SupportInbox /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><Profile /></ProtectedRoute>
      } />

      <Route path="/access-control" element={
        <ProtectedRoute><AccessControl /></ProtectedRoute>
      } />
      <Route path="/agents" element={
        <ProtectedRoute><AgentMarketplace /></ProtectedRoute>
      } />
      <Route path="/agents/marketplace" element={<Navigate to="/agents" replace />} />
      <Route path="/agents/custom" element={<Navigate to="/agents" replace />} />
      <Route path="/agents/professional" element={<Navigate to="/agents" replace />} />
      <Route path="/agents/scientific" element={<Navigate to="/agents" replace />} />
      <Route path="/agents/use/:agentId" element={<Navigate to="/agents" replace />} />
      <Route path="/chats" element={<Navigate to="/" replace />} />

      <Route path="/data" element={<Navigate to="/data-residency" replace />} />
      <Route path="/data-residency" element={<SeoTopicPage pageKey="data-residency" />} />
      <Route path="/security" element={<SeoTopicPage pageKey="security" />} />
      <Route path="/compliance" element={<SeoTopicPage pageKey="compliance" />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/eula" element={<Eula />} />
      <Route path="/about" element={<About />} />
      <Route path="/news" element={<News />} />
      <Route path="/news/:newsId" element={<NewsDetail />} />
      <Route path="/news/:newsId/:slug" element={<NewsDetail />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/insights/:slug" element={<InsightArticlePage />} />
      <Route path="/sovereign-ai-canada" element={<SeoTopicPage pageKey="sovereign-ai-canada" />} />

      <Route path="/ai-model-training" element={<SeoTopicPage pageKey="ai-model-training" />} />
      <Route path="/small-language-model-training" element={<SeoTopicPage pageKey="small-language-model-training" />} />
      <Route path="/private-ai" element={<SeoTopicPage pageKey="private-ai" />} />
      <Route path="/pipeda-compliant-ai" element={<SeoTopicPage pageKey="pipeda-compliant-ai" />} />
      <Route path="/healthcare-ai-canada" element={<SeoTopicPage pageKey="healthcare-ai-canada" />} />
      <Route path="/legal-ai-canada" element={<SeoTopicPage pageKey="legal-ai-canada" />} />
      <Route path="/canadian-ai-infrastructure" element={<SeoTopicPage pageKey="canadian-ai-infrastructure" />} />
      <Route path="/rag-vs-fine-tuning" element={<SeoTopicPage pageKey="rag-vs-fine-tuning" />} />
      <Route path="/canadian-ai-platform" element={<SeoTopicPage pageKey="canadian-ai-platform" />} />
      <Route path="/enterprise-ai" element={<SeoTopicPage pageKey="enterprise-ai" />} />

      <Route path="/purchase" element={<Purchase />} />

      <Route path="/community" element={<Community />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/research" element={<Research />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/enterprise" element={<Partnership />} />
      <Route path="/partnership" element={<Navigate to="/enterprise" replace />} />
      <Route path="/models" element={<Models />} />
      <Route path="/maplept" element={<MaplePT />} />

      <Route path="/mapleos" element={<MapleOS />} />
      <Route path="/maplenode" element={<MapleNode />} />
      <Route path="/credits" element={
        <ProtectedRoute><AICredits /></ProtectedRoute>
      } />
      <Route path="/finetune-chat" element={

        <ProtectedRoute><FinetuneChat /></ProtectedRoute>
      } />
      <Route path="/finetune-chat/:jobId" element={
        <ProtectedRoute><FinetuneChat /></ProtectedRoute>
      } />
      <Route path="/docs" element={<Navigate to="/" replace />} />
      <Route path="/downloads" element={<Navigate to="/research" replace />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      {/* CanXP Code device-login approval. Deliberately NOT ProtectedRoute: the CLI sends people
          here cold, and bouncing a signed-out visitor loses the ?code= that the whole flow needs.
          The page renders for everyone and asks for sign-in itself, carrying the code along. */}
      <Route path="/link" element={<DeviceLink />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}